import {
  boardResponse,
  cancelResponse,
  checkCodeResponse,
  kulcinaState,
  hypeToggleResponse,
  movieSchema,
  movieSearchResponse,
  pitchBoardResponse,
  pitchSchema,
  pitchVoteResponse,
  roomSchema,
  roomMapSchema,
  reserveResponse,
  screeningSeatMapSchema,
  healthSchema,
  sessionResponse,
  titleOrderSchema,
  ticketsResponse,
  putDeskResponse,
  type ApiError,
  type CheckCodeResponse,
  type Guard,
  type Health,
  type KulcinaOp,
  type KulcinaState,
  type SessionResponse,
  type BoardResponse,
  type CreatePitchRequest,
  type CreateRoomRequest,
  type CreateScreeningRequest,
  type Movie,
  type Pitch,
  type PitchBoardResponse,
  type PitchVoteResponse,
  type MovieOverrideRequest,
  type MovieSearchResponse,
  type HypeToggleResponse,
  type Room,
  type RoomMap,
  type CancelResponse,
  type ReserveResponse,
  type ScreeningSeatMap,
  type SeatDraft,
  type TitleLang,
  type UpdateScreeningRequest,
  type PutDeskRequest,
  type PutDeskResponse,
  type Ticket,
} from '@movienight/shared';
import { z } from 'zod';

/**
 * Everything is same-origin: Caddy serves the SPA and proxies /api to Fastify,
 * and in dev Vite proxies the same paths. So there is no base URL constant
 * here, and nothing to change when the site moves from a bare IP to a domain.
 */

/** Mirrors CSRF_HEADER in apps/api/src/app.ts. */
const CSRF_HEADER = 'x-movienight';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    /** Serbian, safe to show the user. */
    message: string,
    /** Structured extras — the door puts `retryAfterSec` here. */
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

export async function request<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, ...rest } = init;
  const method = rest.method ?? (json !== undefined ? 'POST' : 'GET');

  const res = await fetch(path, {
    ...rest,
    method,
    // The session cookie is HttpOnly; the browser must be told to send it.
    credentials: 'same-origin',
    headers: {
      ...(json !== undefined ? { 'content-type': 'application/json' } : {}),
      // Present on every request, not just mutations, so a route that becomes
      // a POST later doesn't start 403ing.
      [CSRF_HEADER]: '1',
      ...rest.headers,
    },
    ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
  });

  if (!res.ok) {
    let code = 'http_error';
    let message = 'Nešto je puklo. Probaj ponovo.';
    let details: unknown;
    try {
      const body = (await res.json()) as ApiError;
      if (body?.error) ({ code, message, details } = body.error);
    } catch {
      /* non-JSON error body; the defaults above are already user-safe */
    }
    throw new ApiRequestError(res.status, code, message, details);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function getHealth(): Promise<Health> {
  return healthSchema.parse(await request<unknown>('/api/health'));
}

// --- the door ---------------------------------------------------------------

/**
 * Asked once on cold entry, before the door renders anything. `greeting`
 * decides between the 1.2-second nod and the full scene.
 */
export async function getSession(): Promise<SessionResponse> {
  return sessionResponse.parse(await request<unknown>('/api/session'));
}

export async function checkCode(code: string): Promise<CheckCodeResponse> {
  return checkCodeResponse.parse(await request<unknown>('/api/door/code', { json: { code } }));
}

export async function doorRegister(body: {
  code: string;
  nickname: string;
  password: string;
}): Promise<void> {
  await request('/api/door/register', { json: body });
}

export async function doorLogin(body: {
  nickname: string;
  password: string;
  remember?: boolean;
}): Promise<{ alreadyBribed: Guard | null }> {
  return request('/api/door/login', { json: body });
}

/**
 * Sends the *choice*, never a duration. `null` is pocketing the bill and is a
 * completely normal answer — the server gives you a 12-hour session and
 * nothing nags you about it.
 */
export async function doorBribe(guard: Guard | null): Promise<{ betrayed: Guard | null }> {
  return request('/api/door/bribe', { json: { guard } });
}

export async function doorLogout(): Promise<void> {
  await request('/api/door/logout', { method: 'POST' });
}

// --- your own settings ------------------------------------------------------

/**
 * Save the language order from the sign at the bottom of the page.
 *
 * The whole list goes up, and the saved list comes back — the client adopts
 * the server's answer rather than assuming its optimistic reorder stuck. Same
 * shape as the hype toggle answering with the authoritative count, and for the
 * same reason: two tabs, one row.
 */
export async function saveTitleOrder(order: readonly TitleLang[]): Promise<TitleLang[]> {
  const body = await request<unknown>('/api/me/title-order', {
    method: 'PUT',
    json: { order },
  });
  return z.object({ order: titleOrderSchema }).parse(body).order;
}

/**
 * Pulls `retryAfterSec` out of a door rejection so the scene can show a
 * countdown in character instead of an error toast.
 */
export function lockoutSeconds(err: unknown): number | null {
  if (!(err instanceof ApiRequestError)) return null;
  const { details } = err;
  if (typeof details === 'object' && details !== null && 'retryAfterSec' in details) {
    const value = (details as { retryAfterSec: unknown }).retryAfterSec;
    if (typeof value === 'number') return value;
  }
  return null;
}

// --- Kulčina's display ------------------------------------------------------

/** The whole grid. Called on mount, and again on socket reconnect as the resync. */
export async function getKulcina(): Promise<KulcinaState> {
  return kulcinaState.parse(await request<unknown>('/api/kulcina'));
}

/**
 * Commit cell operations — one finished stroke, or every cell at once when
 * somebody presses Reset or Clear.
 *
 * Ops, not the whole grid, so two people drawing at once keep both strokes.
 * Sending a grid here would mean whoever's pointer came up last silently
 * erased the other person's line. There is no whole-grid endpoint any more:
 * see the note at the top of apps/api/src/routes/kulcina.ts.
 */
export async function commitKulcinaOps(ops: KulcinaOp[]): Promise<KulcinaState> {
  return kulcinaState.parse(
    await request<unknown>('/api/kulcina', { method: 'PATCH', json: { ops } }),
  );
}

// --- the Now Showing board --------------------------------------------------

/**
 * Everything currently on the board, soonest first.
 *
 * The server decides what "currently" means — a screening stays up until
 * roughly when its credits roll, not until its start time — so there is no
 * client-side filtering here to keep in step with it.
 *
 * This is also the refetch the socket triggers: `screening_announced` carries
 * an id and nothing else, and the answer to it is to ask again.
 */
export async function getBoard(): Promise<BoardResponse> {
  return boardResponse.parse(await request<unknown>('/api/screenings'));
}

/** The admin view: drafts, cancelled and past nights included. */
export async function getAllScreenings(): Promise<BoardResponse> {
  return boardResponse.parse(await request<unknown>('/api/screenings?scope=all'));
}

export async function createScreening(body: CreateScreeningRequest): Promise<{ id: string }> {
  return request('/api/screenings', { json: body });
}

export async function updateScreening(
  id: string,
  body: UpdateScreeningRequest,
): Promise<void> {
  await request(`/api/screenings/${id}`, { method: 'PATCH', json: body });
}

/**
 * Close a wildcard's vote now, by hand.
 *
 * The resolver does this on its own at `pitchClosesAt`; this is the same code
 * path with the due check lifted, for a slot opened without a close time and
 * for the night the argument has gone on long enough. Answers 409 when the
 * vote has already been counted — which is a different thing from a missing
 * screening, and the panel says which.
 */
export async function resolveWildcard(
  screeningId: string,
): Promise<{ screeningId: string; pitchId: string | null; movieId: string | null }> {
  return request(`/api/screenings/${screeningId}/resolve`, { method: 'POST' });
}

// --- rooms and films --------------------------------------------------------

const roomsResponse = z.object({ rooms: z.array(roomSchema) });
const moviesResponse = z.object({ movies: z.array(movieSchema) });
const movieResponse = z.object({ movie: movieSchema });

export async function getRooms(): Promise<Room[]> {
  return roomsResponse.parse(await request<unknown>('/api/rooms')).rooms;
}

export async function createRoom(body: CreateRoomRequest): Promise<Room> {
  return z
    .object({ room: roomSchema })
    .parse(await request<unknown>('/api/rooms', { json: body })).room;
}

export async function getMovies(): Promise<Movie[]> {
  return moviesResponse.parse(await request<unknown>('/api/movies')).movies;
}

/**
 * Admin only. `live: false` means there is no TMDB key configured and these
 * came from the fixture set — the panel says so rather than letting someone
 * wonder why the catalogue is six films long.
 */
export async function searchTmdb(
  q: string,
  signal?: AbortSignal,
): Promise<MovieSearchResponse> {
  return movieSearchResponse.parse(
    await request<unknown>(`/api/movies/search?q=${encodeURIComponent(q)}`, { signal }),
  );
}

/** Idempotent on `tmdbId`: running it again refreshes a film you already have. */
export async function importMovie(tmdbId: number): Promise<Movie> {
  return movieResponse.parse(
    await request<unknown>('/api/movies/import', { json: { tmdbId } }),
  ).movie;
}

/** Hand corrections. Stored separately and applied over the imported values,
 *  so the next import cannot undo them. */
export async function overrideMovie(id: string, body: MovieOverrideRequest): Promise<Movie> {
  return movieResponse.parse(
    await request<unknown>(`/api/movies/${id}`, { method: 'PATCH', json: body }),
  ).movie;
}

// --- seat maps and booking --------------------------------------------------

/** The room and where you can sit in it. What the editor loads. */
export async function getRoomMap(roomId: string): Promise<RoomMap> {
  return roomMapSchema.parse(await request<unknown>(`/api/rooms/${roomId}/map`));
}

/**
 * The picture the map is drawn on.
 *
 * `FormData` and no `content-type`: the browser has to set the header itself
 * so it can append the multipart boundary, and passing `json` here would
 * overwrite it with `application/json` and produce a body Fastify cannot
 * parse. The CSRF header still rides along — `request` adds it to everything.
 */
export async function uploadRoomImage(roomId: string, file: File): Promise<RoomMap> {
  const body = new FormData();
  body.append('image', file);
  return roomMapSchema.parse(
    await request<unknown>(`/api/rooms/${roomId}/image`, { method: 'POST', body }),
  );
}

/**
 * Save the whole map at once. Seats carrying an `id` are updated, the rest are
 * new, and anything the array leaves out is deleted — in one transaction, so
 * the room is never saved half-drawn.
 */
export async function saveSeats(roomId: string, seats: SeatDraft[]): Promise<RoomMap> {
  return roomMapSchema.parse(
    await request<unknown>(`/api/rooms/${roomId}/seats`, { method: 'PUT', json: { seats } }),
  );
}

/**
 * The seat map for one night: the room, the seats, and who is in them. Also
 * the resync — the socket's seat events carry ids, so a client that has been
 * away asks for this again rather than replaying what it missed.
 */
export async function getSeatMap(screeningId: string): Promise<ScreeningSeatMap> {
  return screeningSeatMapSchema.parse(
    await request<unknown>(`/api/screenings/${screeningId}/seats`),
  );
}

/**
 * Claim a seat, or move to the one you claimed instead. One route for both:
 * there is a single reservation row per person per night and moving is that
 * row changing seats, so a client never has to decide which verb applies.
 *
 * `userId` is the host dropping somebody else into a free seat.
 */
export async function reserveSeat(
  screeningId: string,
  seatId: string,
  userId: string | null = null,
): Promise<ReserveResponse> {
  return reserveResponse.parse(
    await request<unknown>(`/api/screenings/${screeningId}/reservation`, {
      method: 'PUT',
      json: { seatId, userId },
    }),
  );
}

/** Give the seat back. The answer says whether it counted as a late cancel. */
export async function cancelReservation(
  screeningId: string,
  userId: string | null = null,
): Promise<CancelResponse> {
  return cancelResponse.parse(
    await request<unknown>(`/api/screenings/${screeningId}/reservation`, {
      method: 'DELETE',
      json: { userId },
    }),
  );
}

/**
 * Light or extinguish your flame. A toggle, not two routes: the page can be
 * stale, and "light this" is an instruction the server would have to refuse,
 * whereas "flip it" is always answerable. The response says what is true now.
 */
export async function toggleHype(screeningId: string): Promise<HypeToggleResponse> {
  return hypeToggleResponse.parse(
    await request<unknown>(`/api/screenings/${screeningId}/hype`, { method: 'POST' }),
  );
}

// --- the Pitch Room ---------------------------------------------------------

/**
 * The corkboard: every open card, what the board is currently playing for, and
 * how many more cards you may pin.
 *
 * Also the resync. The socket's pitch events carry ids and tallies, never the
 * cards themselves, so a client that has been away asks for this again rather
 * than replaying what it missed — the same rule as everywhere else here.
 */
export async function getPitches(): Promise<PitchBoardResponse> {
  return pitchBoardResponse.parse(await request<unknown>('/api/pitches'));
}

/**
 * Pin a card. Exactly one of `tmdbId`, `movieId` and `freeTitle` — a card names
 * one film or carries one title of its own, and the server refuses the rest.
 *
 * A `tmdbId` costs a round trip to TMDB on the way in, because the film is
 * imported so the card can show a poster from our own origin.
 */
export async function createPitch(body: CreatePitchRequest): Promise<Pitch> {
  return z
    .object({ pitch: pitchSchema })
    .parse(await request<unknown>('/api/pitches', { json: body })).pitch;
}

/** Take your own card down. The host may take down anybody's. */
export async function withdrawPitch(id: string): Promise<void> {
  await request(`/api/pitches/${id}`, { method: 'DELETE' });
}

/**
 * Vote, or take your vote back with 0.
 *
 * A PUT carrying the value you want rather than a toggle: three states means
 * "flip it" has no answer, and this way a page that has gone stale still makes
 * an exact statement. Idempotent, so a retry costs nothing.
 */
export async function votePitch(id: string, value: 1 | 0 | -1): Promise<PitchVoteResponse> {
  return pitchVoteResponse.parse(
    await request<unknown>(`/api/pitches/${id}/vote`, { method: 'PUT', json: { value } }),
  );
}

// --- the desk ---------------------------------------------------------------

/** Your tickets, oldest night first. Only ever your own. */
export async function getTickets(): Promise<Ticket[]> {
  return ticketsResponse.parse(await request<unknown>('/api/tickets')).tickets;
}

/**
 * Where some tickets now lie. `keepalive` for the save that happens on the way
 * out — closing the tab, or the desk — which the browser would otherwise
 * cancel along with the page. Its body is capped at 64KB, which is several
 * hundred placements.
 */
export async function saveDesk(
  placements: PutDeskRequest['placements'],
  { keepalive = false }: { keepalive?: boolean } = {},
): Promise<PutDeskResponse> {
  return putDeskResponse.parse(
    await request<unknown>('/api/tickets/desk', {
      method: 'PUT',
      json: { placements },
      keepalive,
    }),
  );
}
